<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getColorFromScore, stripHtmlTags } from '@/utils'
import UIKit from '@pocketprep/ui-kit'
import ContentTab from '@/components/Content/ContentTab.vue'
import { statsModule } from '@/store/stats/module'
import { examModule } from '@/store/exam/module'
import StudentQuestionRow from '@/components/StudentQuestionRow.vue'
import type { IQuizAnswer } from '@pocketprep/types/Study/Cloud'
import type { Study } from '@pocketprep/types'
import type { TStudentQuestionRow, TStudentQuestions } from '@/store/types'

type TStudyActivity = {
    icon?: string
    name: string
    iconColor?: string
    date: string
    year?: string
    score: number | string
    scoreRange: string
    answers?: IQuizAnswer[]
    quizId?: string
    durationSeconds: number
    levelUpQuizProgress?: Study.Class.LevelUpProgress
    assignmentName?: string
    answeredCount?: number
    correctCount?: number
    incorrectCount?: number
}

const {
    SidePanel,
    Icon,
    Table,
    EmptyState,
} = UIKit

const props = withDefaults(
    defineProps<{
        student: string
        examActivity: TStudyActivity
        defaultActiveTab?: 'Answered' | 'Flagged' | 'Incorrect' | 'Correct'
        trapFocus?: boolean
    }>(),
    {
        trapFocus: true,
        defaultActiveTab: undefined,
    }
)

const emit = defineEmits<{
    questionClicked: [payload: { questionSerial: string; type: string }]
    tabClicked: [tab: 'Answered' | 'Flagged' | 'Incorrect' | 'Correct']
    close: [value?: boolean]
    mounted: [value?: boolean]
}>()

const route = useRoute()
const router = useRouter()

const activeTab = ref<'Answered' | 'Flagged' | 'Incorrect' | 'Correct' | ''>('Answered')
const quizId = ref<string | null>(null)
const topContainer = ref<HTMLElement | null>(null)
const topContainerHeight = ref<number | null>(null)

const tableColumns = [{
    name: ' ',
    propName: 'question',
    isSortDisabled: true,
}, {
    name: ' ',
    propName: 'numStudentFlagged',
    isSortDisabled: true,
}]

const studentStats = computed(() => statsModule.getters.getCurrentStudentStats())
const examActivityMode = computed(() =>
    props.examActivity.name === 'Recall Challenge' ? 'Level Up' : props.examActivity.name
)
const examActivityScore = computed(() => props.examActivity.score as number)
const currentExamQuestionsBySerial = computed(() => examModule.getters.getCurrentExamQuestionsBySerial())
const studentFlaggedQuestions = computed(() =>
    studentStats.value.flaggedQuestions.filter(q => !q.isMockQuestion)
)
const isActivityMissingQuizData = computed(() => !props.examActivity.quizId)

const isQuestionFlagged = (serial: string) =>
    !!studentFlaggedQuestions.value.find(question => question.serial === serial)

const clickChangeTab = (tab: 'Answered' | 'Flagged' | 'Incorrect' | 'Correct') => {
    if (isActivityMissingQuizData.value) {
        return
    }
    activeTab.value = tab
    emit('tabClicked', tab)
}

const formattedDuration = computed(() => {
    const durationSeconds = props.examActivity.durationSeconds
    if (durationSeconds < 60) {
        return `${durationSeconds} sec`
    }
    if (durationSeconds > 60 && durationSeconds < 3600) {
        const min = Math.round(durationSeconds / 60)
        return `${min} min`
    }
    if (durationSeconds > 3600) {
        const hours = Math.floor(durationSeconds / 3600)
        const min = Math.round((durationSeconds - (hours * 3600)) / 60)
        return `${hours}h ${min}m`
    }
    return '0'
})

const studentQuestionsForActivity = computed(() => {
    const questionsForActivity: TStudentQuestionRow[] = []
    const answers = props.examActivity.answers || []
    answers.forEach(quizAnswer => {
        const q = currentExamQuestionsBySerial.value[quizAnswer.questionSerial]
        const scenarioPartLabel = q && examModule.getters.getScenarioPartLabel(q)
        const questionPrompt = q ? stripHtmlTags(q.prompt) : ''
        if (q) {
            const s = q.subject as Study.Class.SubjectJSON
            questionsForActivity.push({
                id: Math.random(),
                serial: q.serial,
                subject: s.name,
                prompt: questionPrompt,
                isFlagged: isQuestionFlagged(q.serial),
                isCorrect: quizAnswer.isCorrect,
                isAnswered: true,
                tabIndex: 0,
                scenarioPartLabel,
            })
        }
    })
    return questionsForActivity
})

const questions = computed((): TStudentQuestions =>
    studentQuestionsForActivity.value.reduce<TStudentQuestions>((acc, question) => {
        const serial = question.serial
        const isFlagged = isQuestionFlagged(serial)
        const isAnswered = question.isAnswered
        const isCorrect = question.isCorrect
        const questionRow = {
            id: Math.random(),
            serial: question.serial,
            subject: question.subject,
            prompt: question.prompt,
            isFlagged,
            isCorrect: question.isCorrect,
            isAnswered: question.isAnswered,
            tabIndex: 0,
            scenarioPartLabel: question.scenarioPartLabel,
        }
        if (isFlagged) {
            acc.Flagged?.push(questionRow)
        }
        if (isAnswered) {
            acc.Answered?.push(questionRow)
        }
        if (!isCorrect && isAnswered) {
            acc.Incorrect?.push(questionRow)
        }
        if (isCorrect) {
            acc.Correct?.push(questionRow)
        }
        return acc
    }, { Answered: [], Flagged: [], Incorrect: [], Correct: [] })
)

const rowClicked = (row: { serial: string }) => {
    if (route.matched.some(({ name }) => name === 'view-assignment')) {
        emit('questionClicked', {
            questionSerial: row.serial,
            type: activeTab.value.toLocaleLowerCase(),
        })
    } else {
        router.push({
            name: route.name as string,
            params: { ...route.params },
            query: {
                ...route.query,
                questionSerial: row.serial,
                quizId: quizId.value ?? undefined,
                type: activeTab.value.toLocaleLowerCase(),
            },
        })
    }
}

const levelUpExamActivityLevel = computed(() => props.examActivity.levelUpQuizProgress?.level)
const levelUpExamActivitySubjectId = computed(() => props.examActivity.levelUpQuizProgress?.subjectId)

const numOfLevelsInSubject = computed(() => {
    const currentGroupExam = examModule.getters.getCurrentGroupExam()
    const examMetadataId = currentGroupExam?.examMetadata?.objectId
    if (!examMetadataId || !levelUpExamActivitySubjectId.value) {
        return 0
    }
    const subjectWithLevels = examModule.getters.getSubjectWithLevels(
        examMetadataId,
        levelUpExamActivitySubjectId.value
    )
    return subjectWithLevels?.levels.length || 0
})

const currentQuestionType = computed(() => route.query.type as string | undefined)

const emitClose = () => {
    emit('close', true)
}

const emitMounted = () => {
    emit('mounted', true)
}

const updateActivityState = () => {
    const questionType = currentQuestionType.value
    if (questionType) {
        activeTab.value = (questionType[0] || '')
            .toUpperCase() + questionType.slice(1).toLowerCase() as typeof activeTab.value
    }
}

watch(currentQuestionType, updateActivityState)

onMounted(async () => {
    quizId.value = props.examActivity.quizId ?? null
    if (!props.examActivity.assignmentName) {
        await examModule.actions.fetchSubjectsWithLevels()
    }
    if (props.defaultActiveTab) {
        activeTab.value = props.defaultActiveTab
    }
    if (isActivityMissingQuizData.value) {
        activeTab.value = ''
    }
    await nextTick()
    if (topContainer.value) {
        topContainerHeight.value = topContainer.value.getBoundingClientRect().height
    }
    emitMounted()
})
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            class="exam-activity"
            width="medium"
            name=""
            :isTeachSidePanel="true"
            :trapFocus="trapFocus"
            @mounted="emitMounted()"
            @close="emitClose()"
        >
            <div
                class="exam-activity__top-container"
                ref="topContainer"
            >
                <div
                    class="exam-activity__quiz-results"
                >
                    <div
                        class="exam-activity__quiz-results-name-container"
                    >
                        <div v-if="examActivity.assignmentName" class="exam-activity__quiz-results-assignment-name">
                            {{ examActivity.assignmentName }}
                        </div>
                        <div
                            class="exam-activity__quiz-results-name"
                        >
                            {{ examActivityMode }}
                        </div>
                        <div
                            class="exam-activity__quiz-results-name"
                        >
                            {{ `${examActivityMode === 'Assignment' ? 'Results' : 'Quiz Results'}` }}
                        </div>
                    </div>
                    <div
                        class="exam-activity__quiz-results-stat exam-activity__quiz-results-score-color"
                        :class="getColorFromScore(examActivityScore)"
                    >
                        <span>
                            {{ examActivityScore }}%
                        </span>
                        <label>Quiz Score</label>
                    </div>
                    <div
                        class="exam-activity__quiz-results-stat exam-activity__quiz-results-unanswered"
                    >
                        <span>
                            {{
                                isActivityMissingQuizData
                                    ? examActivity.correctCount
                                    : questions['Correct']?.length
                            }} / {{
                                isActivityMissingQuizData
                                    ? examActivity.answeredCount
                                    : questions['Answered']?.length
                            }}
                        </span>
                        <label>Answered Correctly</label>
                    </div>
                </div>
                <div
                    class="exam-activity__tabs"
                    role="tablist"
                >
                    <ContentTab
                        key="Answered"
                        :is-active="activeTab === 'Answered'"
                        label="Answered"
                        :count="isActivityMissingQuizData ? examActivity.answeredCount : questions['Answered']?.length"
                        :disabled="isActivityMissingQuizData"
                        role="tab"
                        :aria-selected="activeTab === 'Answered' ? 'true' : 'false'"
                        @keydown.enter="clickChangeTab('Answered')"
                        @click="clickChangeTab('Answered')"
                    />
                    <ContentTab
                        v-if="!isActivityMissingQuizData"
                        key="Flagged"
                        :is-active="activeTab === 'Flagged'"
                        label="Flagged"
                        :count="questions['Flagged']?.length"
                        role="tab"
                        :aria-selected="activeTab === 'Flagged' ? 'true' : 'false'"
                        @keydown.enter="clickChangeTab('Flagged')"
                        @click="clickChangeTab('Flagged')"
                    />
                    <ContentTab
                        key="Incorrect"
                        :is-active="activeTab === 'Incorrect'"
                        label="Incorrect"
                        :count="isActivityMissingQuizData
                            ? examActivity.incorrectCount
                            : questions['Incorrect']?.length"
                        :disabled="isActivityMissingQuizData"
                        role="tab"
                        :aria-selected="activeTab === 'Incorrect' ? 'true' : 'false'"
                        @keydown.enter="clickChangeTab('Incorrect')"
                        @click="clickChangeTab('Incorrect')"
                    />
                    <ContentTab
                        key="Correct"
                        :is-active="activeTab === 'Correct'"
                        label="Correct"
                        :count="isActivityMissingQuizData ? examActivity.correctCount : questions['Correct']?.length"
                        :disabled="isActivityMissingQuizData"
                        role="tab"
                        :aria-selected="activeTab === 'Correct' ? 'true' : 'false'"
                        @keydown.enter="clickChangeTab('Correct')"
                        @click="clickChangeTab('Correct')"
                    />
                </div>
            </div>
            <div
                class="exam-activity__questions"
                :style="{
                    marginTop: `${topContainerHeight}px`,
                    height: `calc(100% - ${topContainerHeight}px)`
                }"
            >
                <div
                    v-if="isActivityMissingQuizData"
                    class="exam-activity__empty"
                >
                    <EmptyState
                        class="exam-activity__empty-state"
                        state="answered"
                        title="No available details"
                        message="This student reset their study progress after taking this assignment.
                        We can no longer display the questions answered."
                    >
                        <template #imageRow>
                            <div />
                        </template>
                    </EmptyState>
                </div>
                <div
                    v-else-if="questions['Answered']?.length === 0 && activeTab === 'Answered'"
                    class="exam-activity__empty"
                >
                    <EmptyState
                        class="exam-activity__empty-state"
                        state="answered"
                        title="0 Answered Questions"
                    />
                </div>
                <div
                    v-else-if="questions['Flagged']?.length === 0 && activeTab === 'Flagged'"
                    class="exam-activity__empty"
                >
                    <EmptyState
                        class="exam-activity__empty-state"
                        state="flagged"
                        title="0 Flagged Questions"
                    />
                </div>
                <div
                    v-else-if="questions['Incorrect']?.length === 0 && activeTab === 'Incorrect'"
                    class="exam-activity__empty"
                >
                    <EmptyState
                        class="exam-activity__empty-state"
                        state="incorrect"
                        title="0 Incorrect Questions"
                    />
                </div>
                <div
                    v-else-if="questions['Correct']?.length === 0 && activeTab === 'Correct'"
                    class="exam-activity__empty"
                >
                    <EmptyState
                        class="exam-activity__empty-state"
                        state="correct"
                        title="0 Correct Questions"
                    />
                </div>
                <Table
                    v-if="studentQuestionsForActivity.length"
                    class="exam-activity__table"
                    theme="open"
                    :sort-toggle-order="[ 1, -1 ]"
                    :rows="questions[activeTab] || []"
                    :columns="tableColumns"
                    :show-header="false"
                    :table-content-styles="{
                        boxSizing: 'none',
                    }"
                    :table-row-styles="{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '12px 16px 12px 30px',
                        cursor: 'pointer',
                    }"
                    @rowClicked="rowClicked({serial: $event.serial as string})"
                >
                    <template #tableCellValue="{ row, column }">
                        <StudentQuestionRow
                            class="exam-activity__question-row"
                            :row="row"
                            :column="column"
                        />
                    </template>
                </Table>
            </div>
            <template #sidePanelTitle>
                <div
                    class="exam-activity__title"
                >
                    <div
                        class="exam-activity__name"
                    >
                        {{ student }}
                    </div>
                    <div class="exam-activity__bullet">
                        •
                    </div>
                    <div
                        class="exam-activity__date"
                    >
                        {{ examActivity.date || '' }}, {{ examActivity.year || '' }}
                    </div>
                    <div
                        v-if="examActivity.name === 'Level Up' && levelUpExamActivityLevel && numOfLevelsInSubject"
                        class="exam-activity__level-up-info"
                    >
                        <div class="exam-activity__bullet">
                            •
                        </div>
                        <div class="exam-activity__level">
                            Level {{ levelUpExamActivityLevel }}/{{ numOfLevelsInSubject }}
                        </div>
                    </div>
                    <div
                        v-if="examActivity.name === 'Recall Challenge'"
                        class="exam-activity__level-up-info"
                    >
                        <div class="exam-activity__bullet">
                            •
                        </div>
                        <div class="exam-activity__level">
                            Recall Challenge
                        </div>
                    </div>
                    <div
                        v-if="examActivity.name === 'Timed Quiz' || examActivity.name === 'Assignment'"
                        class="exam-activity__time"
                    >
                        <div
                            class="exam-activity__bullet"
                        >
                            •
                        </div>
                        <Icon
                            class="exam-activity__timer-icon"
                            type="timer"
                        />
                        <div
                            class="exam-activity__duration"
                        >
                            {{ formattedDuration }}
                        </div>
                    </div>
                </div>
            </template>
            <template #sidePanelFooter>
                <div />
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.exam-activity {
    :deep(*) {
        .uikit-side-panel__title-inner {
            font-size: 15px;
            font-weight: 500;
        }
        .uikit-side-panel__head {
            border-bottom: none;
            padding: 28px 0 34px 31px;
        }

        .uikit-side-panel__content {
            padding: 0;
        }

        .student-question-row__prompt {
            width: 356px;
        }

        .uikit-table__rows:nth-child(1) {
            height: 0;
            border-bottom: 1px;
        }

        .uikit-table__rows:nth-child(2) {
            // If mock exam, the max height here should be slightly different
            max-height: 30%;
        }
        .content-tab {
            border-bottom: 4px solid $barely-background;
            &--active {
                border-bottom-color: $brand-black;
                color: $brand-black !important;
            }
        }

        .section-head__title {
            max-width: 200px;
        }
    }

    &__top-container {
        background-color: $barely-background;
        position: fixed;
    }
    &__tabs {
        display: flex;
        border-bottom: 1px solid $gray-divider;
        padding: 65px 0 0 29px;
    }

    &__title {
        display: flex;
        font-size: 15px;
        padding-top: 9px;
        color: $ash;
    }

    &__time {
        display: flex;
    }

    &__bullet {
        padding: 3px 8px;
        font-size: 11px;
        color: $ash;
    }

    &__timer-icon {
        padding: 0.5px 5px 0 0;
        color: $ash;
    }

    &__date,
    &__level {
        font-size: 15px;
        color: $ash;
    }

    &__level-up-info {
        display: flex;
    }

    &__empty {
        background-color: $white;
    }

    &__empty-state {
        margin: 40px auto 0px auto;
    }

    &__subject-scores-head {
        justify-content: start;
        align-items: flex-end;
    }

    &__quiz-results {
        display: flex;
        align-items: flex-end;
        padding: 0 0 0 31px;
    }

    &__quiz-results-assignment-name {
        color: $ash;
        margin-bottom: 8px;
    }

    &__questions {
        overflow-y: scroll;
        margin-top: 183px;
    }

    &__quiz-results-name {
        font-size: 22px;
        font-weight: 600;
        line-height: 30px;
        width: 190px;
        display: flex;
    }

    &__quiz-results-unanswered {
        min-width: 130px;
    }

    &__quiz-results-stat {
        font-size: 24px;
        text-align: center;
        letter-spacing: 0.24px;
        width: 100px;
        padding-top: 8px;

        &:last-child {
            margin-right: 0;
        }

        span {
            font-weight: 600;
        }

        label {
            white-space: nowrap;
        }
    }

    &__quiz-results-score-color {
        position: relative;

        &::before {
            content: '';
            position: absolute;
            left: 50%;
            top: 0px;
            width: 94px;
            height: 44px;
            margin-left: -52px;
        }

        &.green::before {
            background: url('@/assets/images/highlight-green.svg') no-repeat center center;
        }

        &.red::before {
            background: url('@/assets/images/highlight-red.svg') no-repeat center center;
        }

        &.yellow::before {
            background: url('@/assets/images/highlight-yellow.svg') no-repeat center center;
        }
    }

    &__head-ratio-label {
        flex: 1;
        text-align: right;
        margin-right: 19px;
        font-size: 13px;
        color: $moonlit-ocean;
    }

    &__head-score-label {
        max-width: 34px;
        font-size: 13px;
        color: $moonlit-ocean;
        justify-content: flex-end;
    }

    &__subject-name {
        font-size: 15px;
        line-height: 20px;
        flex: 2;
        min-width: 280px;
        color: $brand-black;
    }

    &__subject-ratio {
        font-weight: 500;
        text-align: right;
        min-width: 53px;
        font-size: 16px;
        letter-spacing: -0.3px;
        color: $brand-black;
    }

    &__subject-score-percent {
        font-weight: 600;
        font-size: 16px;
        margin-right: 5px;
        text-align: right;
        width: 40px;
        color: $brand-black;
    }

    &__percentage-sign {
        font-size: 16px;
        font-weight: 500;
        margin-left: -3px;
    }

    &__expand {
        margin: 0 0 0 36px;

        &--expanded {
            svg {
                transform: rotate(180deg);
            }
        }
    }

    label {
        font-size: 12px;
        line-height: 15.71px;
        display: block;
        margin-top: 5px
    }

    ul {
        padding: 0;
        margin: 0;
        list-style-type: none;

        li {
            padding: 11px 10px 10px 7px;
            margin: 0;
            color: $slate-03;
            display: flex;
            width: 100%;
            position: relative;
            align-items: center;
            border-bottom: 1px solid $gray-divider;
            height: 18.45px;

            &::before {
                content: '';
                width: calc(100% + 28px);
                height: 100%;
                z-index: -1;
                position: absolute;
                left: -14px;
                top: 0;
                display: none;
            }

            &:hover::before {
                display: block;
            }
        }
    }
}
</style>
